import React from 'react';

import {
  Link,
  LinkType,
  LinkTarget,
  BaseProps as LinkBaseProps,
} from '../../components/Link/Link';
import {
  RedeemBalanceCheckArrowLink,
} from '../../components/RedeemBalanceCheckArrowLink/RedeemBalanceCheckArrowLink';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { RedeemMessages, RedeemSignals } from '../../types/Messages';
import { regex } from '../../utils/regex/regex';

type UseRedeemLinkMessagesResult = {
  isExternalLinks: boolean;
  onRedeemLinkClick: (value: string) => void;
};

type UseRedeemLinkParams = {
  redirect: string;
  reserveRedirect?: string;
};

type UseRedeemLinkResult = {
  handleInternalRedirectClick: () => void;
  isInternalRedirect: boolean;
  resultingRedirect: string;
};

export const hooks = {
  useRedeemLinkMessages(): UseRedeemLinkMessagesResult {
    const isExternalLinks = messageHooks.useSubjectState(RedeemMessages.IsExternalLinks);
    const onRedeemLinkClick = messageHooks.useNextSignal(RedeemSignals.OnRedeemLinkClick);

    return {
      isExternalLinks,
      onRedeemLinkClick,
    };
  },

  useRedeemLink({
    redirect,
    reserveRedirect,
  }: UseRedeemLinkParams): UseRedeemLinkResult {
    const {
      isExternalLinks,
      onRedeemLinkClick,
    } = hooks.useRedeemLinkMessages();

    return React.useMemo(() => {
      const isReserveRedirect = reserveRedirect?.length && isExternalLinks;

      return {
        resultingRedirect: reserveRedirect && isReserveRedirect ? reserveRedirect : redirect,
        isInternalRedirect: regex.isInternalHref(
          reserveRedirect && isReserveRedirect ? reserveRedirect : redirect,
        ),
        handleInternalRedirectClick: () => onRedeemLinkClick(redirect),
      };
    }, [isExternalLinks, onRedeemLinkClick, redirect, reserveRedirect]);
  },
};

export type Props = LinkBaseProps & {
  backArrowLinkTitle?: string | JSX.Element;
  isBackArrow?: boolean;
  redirect: string;
  reserveRedirect?: string;
};

export const RedeemLinkContainer = React.memo((props: Props) => {
  const {
    redirect,
    reserveRedirect,
    isBackArrow = false,
    backArrowLinkTitle,
    children,
    className,
    ...rest
  } = props;

  const {
    resultingRedirect,
    isInternalRedirect,
    handleInternalRedirectClick,
  } = hooks.useRedeemLink({
    redirect,
    reserveRedirect,
  });

  return isBackArrow
    ? <RedeemBalanceCheckArrowLink
      onClick={isInternalRedirect ? handleInternalRedirectClick : undefined}
      redirect={isInternalRedirect ? undefined : resultingRedirect}
      title={backArrowLinkTitle}
      className={className}
    />

    : isInternalRedirect ? (
      <Link
        type={LinkType.Span}
        onClick={handleInternalRedirectClick}
        className={className}
        {...rest}
      >
        {children}
      </Link>
    ) : (
      <Link
        type={LinkType.ExternalHref}
        to={resultingRedirect}
        className={className}
        {...rest}
        target={LinkTarget.Blank}
      >
        {children}
      </Link>
    );
});

RedeemLinkContainer.displayName = 'RedeemLinkContainer';
